import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ApiKey } from '../../_shared/domain/api-key.model';
import type { EditApiKeyError } from '../domain/edit-api-key-error';
import { initialState } from './edit-api-key.state';

/**
 * Edit API key slice. Reducers are pure state transitions only; the I/O (load /
 * update) lives in the saga, which dispatches these lifecycle transitions as it
 * progresses. The three field reducers each clear their own validation error.
 */
const slice = createSlice({
  name: 'editApiKey',
  initialState,
  reducers: {
    // ── Load lifecycle (dispatched by the saga) ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<ApiKey>) => {
      const key = action.payload;
      state.apiKey = key;
      state.draft = { name: key.name, scopes: key.scopes, status: key.status };
      state.loadStatus = 'loaded';
    },
    loadFailed: (state, action: PayloadAction<EditApiKeyError>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },
    // ── Field edits ──
    nameChanged: (state, action: PayloadAction<string>) => {
      state.draft.name = action.payload;
      delete state.validationErrors.name;
    },
    scopesChanged: (state, action: PayloadAction<string[]>) => {
      state.draft.scopes = action.payload;
      delete state.validationErrors.scopes;
    },
    statusChanged: (state, action: PayloadAction<string>) => {
      state.draft.status = action.payload;
      delete state.validationErrors.status;
    },
    // ── Save lifecycle ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
    },
    saveFailed: (state, action: PayloadAction<EditApiKeyError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset everything to the initial state (on unmount / navigation away). */
    resetForm: () => initialState,
  },
});

export const editApiKeyActions = slice.actions;
export const editApiKeyReducer = slice.reducer;
